import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import type { CartFeedbackAddToCartProps } from '@design-system/components/Cart/CartFeedbackAddToCart.props'
import type { LineItem } from '@commercelayer/sdk'
import defu from 'defu'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import { useCartAlgoliaProducts } from '@integration-layer/composables/useCart'
import type { Website } from '@design-system/index'
import { canEditProductVariant } from '@design-system/utils/canEditProductVariant'

/**
 * Represents the payload for adding an item to the cart.
 * @typedef {Object} AddToCartPayload
 * @property {string} name - The name of the product.
 * @property {string} sku_code - The unique SKU code of the product.
 * @property {number} [quantity=1] - The quantity of the product to add (defaults to 1).
 * @property {Object} metadata - Additional metadata for the product.
 * @property {string} metadata.size - The size of the product.
 * @property {string} [metadata.color] - The color of the product (optional).
 * @property {Website} [metadata.website] - The website associated with the product.
 * @property {string} [metadata.queryID] - The query ID for tracking (optional).
 */
export type AddToCartPayload = {
  name: string
  sku_code: string
  quantity?: number
  metadata: {
    size: string
    color?: string
    website?: Website
    queryID?: string
    product_from_chat?: boolean
  }
}

/**
 * Composable for managing add to cart functionality.
 * Handles adding products to the cart, tracking loading state, and managing cart-related dialogs.
 *
 * @returns {Object} An object with add to cart methods and state
 * @property {Ref<AddToCartPayload | null>} addToCartPayload - The current payload for adding to cart
 * @property {Function} addToCart - Method to add an item to the cart
 * @property {Ref<boolean>} isLoading - Loading state of the add to cart process
 * @property {Ref<boolean>} error - Error state of the add to cart process
 * @property {Ref<ProductCardHorizontalProps & CartFeedbackAddToCartProps | null>} lastAddedToCart - Details of the last added cart item
 *
 * @example
 * const { addToCart, addToCartPayload, isLoading } = useAddToCart()
 * addToCartPayload.value = {
 *   name: 'Product Name',
 *   sku_code: 'PRODUCT-SKU',
 *   quantity: 1,
 *   metadata: { size: 'M', color: 'Blue' }
 * }
 * await addToCart()
 */
export const useAddToCart = () => {
  const { addToCart, lineItems, lastAdded } = useCart()
  const { openDialog, closeDialog } = useDialog()

  const sizeSelectorSideSlideId = SIDEBAR_IDS.pdpEcommerceBlockSizeSelector
  const { pdpPath } = useRouteHelper()

  const isLoading = useState('isLoading', () => false)

  const error = ref(false)
  const addToCartPayload = useState<null | AddToCartPayload>(
    'addToCartPayload',
    () => null
  )
  const lastAddedToCart = useState<
    null | (ProductCardHorizontalProps & CartFeedbackAddToCartProps)
  >('lastAddedToCart', () => null)

  /**
   * Transforms a line item into a product card horizontal props object.
   *
   * @param {LineItem & { path?: string }} lineItem - The line item to transform
   * @returns {ProductCardHorizontalProps} Transformed product card props
   *
   * @private
   */
  const getLastAddedToCart = (
    lineItem: LineItem & { path?: string }
  ): ProductCardHorizontalProps => ({
    id: lineItem.id,
    info: {
      name: lineItem.name!,
      price: lineItem.unit_amount_float!,
      oldPrice:
        lineItem.compare_at_amount_float !== lineItem.unit_amount_float
          ? lineItem.compare_at_amount_float!
          : 0,
      percentageDiscount: percentageDiscount(
        lineItem.total_amount_float!,
        lineItem.total_amount_float! + lineItem.discount_float!
      ),
      color: lineItem.metadata?.color,
      size: lineItem.metadata?.size,
    },
    path: lineItem?.path ?? '',
    available:
      lastAdded.value?.item.inventory?.available! &&
      lastAdded.value?.item.inventory?.quantity > 0,
    quantity: lineItem.quantity,
    metadata: lineItem.metadata?.website,
    image:
      lineItem.image_url ?? '/images/product-card-horizontal-placeholder.png',
    skuCode: lineItem.sku_code!,
  })

  const { currentWebsite } = useWebsite()

  /**
   * Adds an item to the cart with error handling and state management.
   *
   * @async
   * @throws {Error} If there's an issue adding the item to the cart
   * @returns {Promise<void>}
   *
   * @private
   */
  const addToCart_ = async (options?: { skipFeedback?: boolean }) => {
    if (!addToCartPayload.value) {
      openDialog(sizeSelectorSideSlideId)
      return
    }

    try {
      isLoading.value = true
      error.value = false
      const isAlreadyAdded = lineItems.value.some(
        ({ sku_code }) => sku_code === addToCartPayload.value?.sku_code
      )
      const { quantity, ...attributes } = addToCartPayload.value
      const lineItem = await addToCart(
        defu(attributes, {
          metadata: { website: currentWebsite.value },
        }),
        quantity
      )

      if (lineItem) {
        const algoliaProducts = useCartAlgoliaProducts()

        const algoliaProduct =
          algoliaProducts.value?.[getSkuWithoutSize(lineItem.sku_code)!]

        const path = algoliaProduct ? pdpPath(algoliaProduct) : ''
        lastAddedToCart.value = {
          ...getLastAddedToCart(lineItem),
          isAlreadyAdded,
          path,
          finalSales: algoliaProduct?.FinalSales,
          isFriendsFamily: algoliaProduct?.isFriendsFamily,
          canEditVariant: canEditProductVariant(algoliaProduct),
        }
        if (!options?.skipFeedback) {
          openDialog(SIDEBAR_IDS.cartFeedbackAddToCart)
        }
      }
    } catch (err) {
      error.value = true
    } finally {
      isLoading.value = false
      closeDialog(sizeSelectorSideSlideId)
    }
  }

  return {
    addToCartPayload,
    addToCart: addToCart_,
    isLoading,
    error,
    lastAddedToCart,
  }
}
